import { Client } from '../client/classes/client';
import { Betriebskontakt } from '../stammdaten/betriebskontakte/betriebskontakt';
import { Tierliste } from './classes/tierliste';

/** Minimal fields for Ausstaller/Zielbetrieb display in lists and dashboard rows. */
export type TierlisteAbgangZielDisplaySource = {
    kunden_abgang?: string | null;
    kunde_abgang?: string | null;
    kunde_zielbetrieb?: string | null;
    kunde_einstallen?: string | null;
};

/** Ausstallender name as shown in lists (prefers aggregated `kunden_abgang`). */
export function getTierlisteAusstallerAnzeigeName(
    tierliste: TierlisteAbgangZielDisplaySource | null | undefined,
): string {
    if (!tierliste) {
        return '';
    }
    return (tierliste.kunden_abgang || tierliste.kunde_abgang || '').trim();
}

/** Display name for Ausstallen header / cards (matches backend bbt_agg priority when fresh). */
export function getTierlisteZielbetriebAnzeigeName(
    tierliste: TierlisteAbgangZielDisplaySource | null | undefined,
): string {
    if (!tierliste) {
        return '';
    }
    const ziel = (tierliste.kunde_zielbetrieb || '').trim();
    const ein = (tierliste.kunde_einstallen || '').trim();
    return ziel || ein;
}

/** True when list row should show Ausstaller → Zielbetrieb (distinct names). */
export function hasTierlisteAbgangZielRoute(
    tierliste: TierlisteAbgangZielDisplaySource | null | undefined,
): boolean {
    const ausstaller = getTierlisteAusstallerAnzeigeName(tierliste);
    const ziel = getTierlisteZielbetriebAnzeigeName(tierliste);
    return ausstaller.length > 0 && ziel.length > 0 && ausstaller !== ziel;
}

/** Import lists: show route when Ausstaller and Zielbetrieb are both known. */
export function shouldShowImportTierlisteZielRoute(
    tierliste: TierlisteAbgangZielDisplaySource | null | undefined,
): boolean {
    const ausstaller = getTierlisteAusstallerAnzeigeName(tierliste);
    const ziel = getTierlisteZielbetriebAnzeigeName(tierliste);
    return ausstaller.length > 0 && ziel.length > 0;
}

export function getTierlisteZielbetriebAnzeigeZusatz(tierliste: Tierliste | null | undefined): string {
    if (!tierliste) {
        return '';
    }
    const zielName = (tierliste.kunde_zielbetrieb || '').trim();
    const displayName = getTierlisteZielbetriebAnzeigeName(tierliste);
    if (displayName && displayName === zielName) {
        return (tierliste.kunde_zusatz_zielbetrieb || '').trim();
    }
    return (tierliste.kunde_zusatz_einstallen || '').trim();
}

const HAENDLER_BETRIEBSART_ID = 9002;
const SCHLACHTHOF_BETRIEBSART_ID = 9001;

export function isHaendlerBetriebskontakt(kontakt: Betriebskontakt): boolean {
    return Number(kontakt.betriebsart_id) === HAENDLER_BETRIEBSART_ID;
}

function isSchlachthofBetriebskontakt(kontakt: Betriebskontakt): boolean {
    return Number(kontakt.betriebsart_id) === SCHLACHTHOF_BETRIEBSART_ID;
}

function isFinalEmpfaengerBetriebskontakt(kontakt: Betriebskontakt): boolean {
    if (Number(kontakt.kunde_id) <= 0) {
        return false;
    }
    const betriebsartId = Number(kontakt.betriebsart_id);
    if (betriebsartId === HAENDLER_BETRIEBSART_ID || betriebsartId === SCHLACHTHOF_BETRIEBSART_ID) {
        return false;
    }
    return true;
}

/** True multi-hop: exactly one Händler (Über) and one final farm — triggers DB auto-follow-up list on import. */
export function resolveExternMultiHopKundeIds(
    kontakte: Betriebskontakt[],
): { kunde_id_einstallen: number; kunde_id_zielbetrieb: number } | null {
    const haendler = (kontakte || []).filter(isHaendlerBetriebskontakt);
    const finals = (kontakte || []).filter(isFinalEmpfaengerBetriebskontakt);
    if (haendler.length !== 1 || finals.length !== 1) {
        return null;
    }
    const kunde_id_einstallen = Number(haendler[0].kunde_id);
    const kunde_id_zielbetrieb = Number(finals[0].kunde_id);
    if (kunde_id_einstallen <= 0 || kunde_id_zielbetrieb <= 0) {
        return null;
    }
    return { kunde_id_einstallen, kunde_id_zielbetrieb };
}

/**
 * Master parity for extern Ausstallen: recipients live in betriebskontakte_tierliste only.
 * `kunde_id_zielbetrieb` is set only for true multi-hop (1 Händler + 1 final farm).
 */
export function applyExternEmpfaengerPersistFields(tierliste: Tierliste, kontakte: Betriebskontakt[]): void {
    const multiHop = resolveExternMultiHopKundeIds(kontakte);
    if (multiHop) {
        tierliste.kunde_id_einstallen = multiHop.kunde_id_einstallen;
        tierliste.kunde_id_zielbetrieb = multiHop.kunde_id_zielbetrieb;
        return;
    }
    tierliste.kunde_id_einstallen = null as unknown as number;
    tierliste.kunde_id_zielbetrieb = null as unknown as number;
}

export function applySchlachthofPersistField(tierliste: Tierliste, schlachthof: Client | null | undefined): void {
    const schlachthofId = Number(schlachthof?.kunde_id);
    tierliste.kunde_id_schlachthof = schlachthofId > 0 ? schlachthofId : (null as unknown as number);
}

function clearSchlachthofDisplayFields(tierliste: Tierliste): void {
    tierliste.kunde_schlachthof = null as unknown as string;
    tierliste.kunde_zusatz_schlachthof = undefined;
    tierliste.kunde_vvvo_schlachthof = undefined;
    tierliste.kunde_strasse_schlachthof = undefined;
    tierliste.kunde_plz_schlachthof = undefined;
    tierliste.kunde_ort_schlachthof = undefined;
}

function applySchlachthofDisplayToTierliste(tierliste: Tierliste, schlachthof: Client): void {
    const name = (schlachthof.bezeichnung_final || schlachthof.bezeichnung || '').trim();
    const zusatz = (schlachthof.zusatz_final || schlachthof.zusatz || '').trim();
    tierliste.kunde_schlachthof = name;
    tierliste.kunde_zusatz_schlachthof = zusatz || undefined;
    if (schlachthof.vvvo) {
        tierliste.kunde_vvvo_schlachthof = schlachthof.vvvo;
    }
    if (schlachthof.strasse) {
        tierliste.kunde_strasse_schlachthof = schlachthof.strasse;
    }
    if (schlachthof.plz) {
        tierliste.kunde_plz_schlachthof = schlachthof.plz;
    }
    if (schlachthof.ort) {
        tierliste.kunde_ort_schlachthof = schlachthof.ort;
    }
}

function applySchlachthofPatch(merged: Tierliste, schlachthof: Client | null): void {
    applySchlachthofPersistField(merged, schlachthof);
    if (schlachthof?.kunde_id) {
        applySchlachthofDisplayToTierliste(merged, schlachthof);
        return;
    }
    clearSchlachthofDisplayFields(merged);
}

function applyKontaktDisplayToTierliste(
    tierliste: Tierliste,
    kontakt: Betriebskontakt,
    target: 'einstallen' | 'zielbetrieb',
): void {
    const name = (kontakt.bezeichnung_final || kontakt.bezeichnung || '').trim();
    const zusatz = (kontakt.zusatz_final || kontakt.zusatz || '').trim();
    if (target === 'einstallen') {
        tierliste.kunde_einstallen = name;
        tierliste.kunde_zusatz_einstallen = zusatz || undefined;
        if (kontakt.vvvo) {
            tierliste.kunde_vvvo_einstallen = kontakt.vvvo;
        }
        return;
    }
    tierliste.kunde_zielbetrieb = name;
    tierliste.kunde_zusatz_zielbetrieb = zusatz || undefined;
    if (kontakt.vvvo) {
        tierliste.kunde_vvvo_zielbetrieb = kontakt.vvvo;
    }
    if (kontakt.strasse_final || kontakt.strasse) {
        tierliste.kunde_strasse_zielbetrieb = kontakt.strasse_final || kontakt.strasse;
    }
    const plz = kontakt.plz_final || kontakt.plz;
    const ort = kontakt.ort_final || kontakt.ort;
    if (plz) {
        tierliste.kunde_plz_zielbetrieb = plz;
    }
    if (ort) {
        tierliste.kunde_ort_zielbetrieb = ort;
    }
}

function clearExternEmpfaengerDisplayFields(tierliste: Tierliste): void {
    tierliste.kunde_einstallen = null as unknown as string;
    tierliste.kunde_zielbetrieb = null as unknown as string;
    tierliste.kunde_zusatz_einstallen = undefined;
    tierliste.kunde_zusatz_zielbetrieb = undefined;
    tierliste.kunde_vvvo_einstallen = undefined;
    tierliste.kunde_vvvo_zielbetrieb = undefined;
    tierliste.kunde_strasse_zielbetrieb = undefined;
    tierliste.kunde_plz_zielbetrieb = undefined;
    tierliste.kunde_ort_zielbetrieb = undefined;
}

/** UI patch after read/save — display labels only; persist IDs via `applyExternEmpfaengerPersistFields`. */
export function patchTierlisteFromExternEmpfaenger(
    liste: Tierliste,
    kontakte: Betriebskontakt[],
    schlachthof: Client | null,
): Tierliste {
    const merged: Tierliste = { ...liste };
    const multiHop = resolveExternMultiHopKundeIds(kontakte);

    if (multiHop) {
        const haendler = kontakte.find(isHaendlerBetriebskontakt);
        const finalKontakt = kontakte.find(isFinalEmpfaengerBetriebskontakt);
        if (haendler) {
            applyKontaktDisplayToTierliste(merged, haendler, 'einstallen');
        }
        if (finalKontakt) {
            applyKontaktDisplayToTierliste(merged, finalKontakt, 'zielbetrieb');
        }
        merged.kunde_id_einstallen = multiHop.kunde_id_einstallen;
        merged.kunde_id_zielbetrieb = multiHop.kunde_id_zielbetrieb;
        applySchlachthofPatch(merged, schlachthof);
        return merged;
    }

    applyExternEmpfaengerPersistFields(merged, kontakte);
    clearExternEmpfaengerDisplayFields(merged);

    if (kontakte?.length > 0) {
        applyKontaktDisplayToTierliste(merged, kontakte[0], 'einstallen');
    } else if (schlachthof?.kunde_id) {
        const name = (schlachthof.bezeichnung_final || schlachthof.bezeichnung || '').trim();
        const zusatz = (schlachthof.zusatz_final || schlachthof.zusatz || '').trim();
        merged.kunde_einstallen = name;
        merged.kunde_zusatz_einstallen = zusatz || undefined;
    }
    applySchlachthofPatch(merged, schlachthof);
    return merged;
}

/** Source-group kunde_id for client-side `istTierlisteExtern` (mirrors DB when tiere are on the list). */
export function resolveGruppeKundeIdForTierlisteExtern(
    tierliste: Tierliste | null | undefined,
    gruppeKundeId?: number | null,
): number {
    const fromGruppe = Number(gruppeKundeId);
    if (fromGruppe > 0) {
        return fromGruppe;
    }
    const fromAbgang = Number(tierliste?.kunde_id_abgang);
    return fromAbgang > 0 ? fromAbgang : 0;
}

/**
 * Client mirror of DB `istTierlisteExtern`:
 * extern (1) when `kunde_id_einstallen` differs from source farm OR is NULL
 * (Schlachthof/Kontakte clear einstallen — still extern).
 * Same-kunde (0) only when einstallen is set and equals the source group farm.
 *
 * HIT UI must not use this alone for visibility: without a destination yet,
 * combine with `hasExternEmpfaenger` / Zielbetrieb so HIT is not prompted prematurely.
 */
export function resolveTierlisteExtern(
    tierliste: Tierliste | null | undefined,
    gruppeKundeId?: number | null,
): 0 | 1 {
    const zielKundeId = Number(tierliste?.kunde_id_einstallen);
    // Match DB: ISNULL(kunde_id_einstallen) → extern
    if (zielKundeId <= 0) {
        return 1;
    }
    const sourceKundeId = resolveGruppeKundeIdForTierlisteExtern(tierliste, gruppeKundeId);
    if (sourceKundeId <= 0) {
        return Number(tierliste?.tierliste_extern ?? 0) > 0 ? 1 : 0;
    }
    return zielKundeId !== sourceKundeId ? 1 : 0;
}

/** True only for same-farm transfer (einstallen set and equals source). */
export function istTierlisteDerselbeKunde(
    tierliste: Tierliste | null | undefined,
    gruppeKundeId?: number | null,
): boolean {
    const zielKundeId = Number(tierliste?.kunde_id_einstallen);
    if (zielKundeId <= 0) {
        return false;
    }
    return resolveTierlisteExtern(tierliste, gruppeKundeId) === 0;
}

/** Persist fields for intern Umstallen (kunde_id_einstallen on tierliste). */
export function applyInternZielbetriebPersistFields(
    tierliste: Tierliste,
    betrieb: Client | null,
    gruppeKundeId?: number | null,
): void {
    if (betrieb?.kunde_id) {
        const label = (betrieb.bezeichnung_final || betrieb.bezeichnung || '').trim();
        const zusatz = (betrieb.zusatz_final || betrieb.zusatz || '').trim();
        tierliste.kunde_id_einstallen = betrieb.kunde_id;
        tierliste.kunde_id_zielbetrieb = undefined;
        tierliste.kunde_einstallen = label;
        tierliste.kunde_zusatz_einstallen = zusatz || undefined;
        tierliste.kunde_vvvo_einstallen = betrieb.vvvo || undefined;
        tierliste.kunde_strasse_einstallen = betrieb.strasse || undefined;
        tierliste.kunde_plz_einstallen = betrieb.plz || undefined;
        tierliste.kunde_ort_einstallen = betrieb.ort || undefined;
        tierliste.tierliste_extern = resolveTierlisteExtern(tierliste, gruppeKundeId);
        tierliste.haendler_uebernehmen = 0;
        return;
    }
    tierliste.kunde_id_einstallen = null as unknown as number;
    tierliste.kunde_id_zielbetrieb = undefined;
    tierliste.kunde_einstallen = undefined;
    tierliste.kunde_zusatz_einstallen = undefined;
    tierliste.kunde_vvvo_einstallen = undefined;
    tierliste.kunde_strasse_einstallen = undefined;
    tierliste.kunde_plz_einstallen = undefined;
    tierliste.kunde_ort_einstallen = undefined;
}

export function patchTierlisteFromInternZielbetrieb(
    liste: Tierliste,
    betrieb: Client | null,
    gruppeKundeId?: number | null,
): Tierliste {
    const merged: Tierliste = { ...liste };
    applyInternZielbetriebPersistFields(merged, betrieb, gruppeKundeId);
    return merged;
}

export function hasExternEmpfaenger(
    tierliste: Tierliste | null | undefined,
    kontakte: Betriebskontakt[],
    schlachthof: Client | null,
): boolean {
    if (schlachthof?.kunde_id) {
        return true;
    }
    if (Number(tierliste?.kunde_id_schlachthof) > 0) {
        return true;
    }
    if (kontakte?.length > 0) {
        return true;
    }
    const zielName = getTierlisteZielbetriebAnzeigeName(tierliste);
    if (zielName.length > 0 && zielName !== '—') {
        return true;
    }
    return Number(tierliste?.kunde_id_zielbetrieb) > 0 || Number(tierliste?.kunde_id_einstallen) > 0;
}

export interface ExternEmpfaengerDisplayEntry {
    name: string;
    detailLines: string[];
}

/** API may return geloescht as number 0 or string "0"; only non-zero means soft-deleted. */
export function isAktiveBetriebskontakteTierlisteVerbindung(v: { geloescht?: unknown }): boolean {
    return Number(v?.geloescht ?? 0) === 0;
}

export function unwrapBetriebskontakteTierlisteReadResponse(result: unknown): any[] {
    if (Array.isArray(result)) {
        return result;
    }
    const records = (result as { records?: unknown[] })?.records;
    return Array.isArray(records) ? records : [];
}

export function filterAktiveBetriebskontakteTierlisteVerbindungen(verbindungen: unknown[]): any[] {
    return (verbindungen || []).filter(v => isAktiveBetriebskontakteTierlisteVerbindung(v as { geloescht?: unknown }));
}

/** Dashboard row `herkunft` for Ausstallen lists all BBT Empfänger (comma-separated). */
export function parseEmpfaengerDisplayEntriesFromHerkunft(herkunft: string | null | undefined): ExternEmpfaengerDisplayEntry[] {
    const raw = (herkunft || '').trim();
    if (!raw || raw === '—') {
        return [];
    }
    return raw
        .split(', ')
        .map(label => label.trim())
        .filter(Boolean)
        .map(name => ({ name, detailLines: [] }));
}

/** Display label matching dashboard BBT aggregation (`Name - Zusatz`). */
export function formatBetriebskontaktDisplayName(
    kontakt: Pick<Betriebskontakt, 'bezeichnung' | 'bezeichnung_final' | 'zusatz' | 'zusatz_final'>,
): string {
    const name = (kontakt.bezeichnung_final || kontakt.bezeichnung || '').trim();
    const zusatz = (kontakt.zusatz_final || kontakt.zusatz || '').trim();
    if (name && zusatz) {
        return `${name} - ${zusatz}`;
    }
    return name || zusatz;
}

/** One BBT row → one kontakt; unique by kontakt_id (not kunde_id). */
export function mapBetriebskontakteTierlisteVerbindungenToKontakte(verbindungen: any[]): Betriebskontakt[] {
    const byKontaktId = new Map<number, Betriebskontakt>();
    for (const v of verbindungen || []) {
        const kontaktId = Number(v.kontakt_id);
        if (kontaktId <= 0 || byKontaktId.has(kontaktId)) {
            continue;
        }
        const kundeId = Number(v.kunde_id ?? v.kunden_id);
        if (kundeId <= 0) {
            continue;
        }
        const bezeichnung = (v.bezeichnung_final || v.bezeichnung || '').trim();
        const zusatz = (v.zusatz_final || v.zusatz || '').trim();
        byKontaktId.set(kontaktId, {
            kontakt_id: kontaktId,
            kunde_id: kundeId,
            bezeichnung,
            bezeichnung_final: bezeichnung,
            zusatz: zusatz || undefined,
            zusatz_final: zusatz || undefined,
            vvvo: v.vvvo,
            strasse: v.strasse,
            plz: v.plz,
            ort: v.ort,
            strasse_final: v.strasse,
            plz_final: v.plz,
            ort_final: v.ort,
            betriebsart_id: v.betriebsart_id != null ? Number(v.betriebsart_id) : undefined,
        } as Betriebskontakt);
    }
    return Array.from(byKontaktId.values());
}

type EmpfaengerAddressSource = {
    vvvo?: string | null;
    strasse?: string | null;
    strasse_final?: string | null;
    plz?: string | null;
    plz_final?: string | null;
    ort?: string | null;
    ort_final?: string | null;
};

function buildEmpfaengerDetailLines(source: EmpfaengerAddressSource): string[] {
    const lines: string[] = [];
    const vvvo = (source.vvvo || '').trim();
    if (vvvo) {
        lines.push(`VVVO: ${vvvo}`);
    }
    const strasse = (source.strasse_final || source.strasse || '').trim();
    if (strasse) {
        lines.push(strasse);
    }
    const plzOrt = [(source.plz_final || source.plz || '').trim(), (source.ort_final || source.ort || '').trim()]
        .filter(Boolean)
        .join(' ');
    if (plzOrt) {
        lines.push(plzOrt);
    }
    return lines;
}

function buildTierlisteEmpfaengerDetailLines(tierliste: Tierliste | null | undefined): string[] {
    if (!tierliste) {
        return [];
    }
    return buildEmpfaengerDetailLines({
        vvvo: tierliste.kunde_vvvo_zielbetrieb,
        strasse: tierliste.kunde_strasse_zielbetrieb,
        plz: tierliste.kunde_plz_zielbetrieb,
        ort: tierliste.kunde_ort_zielbetrieb,
    });
}

function supplementDetailLinesFromTierliste(
    detailLines: string[],
    kundeId: number | null | undefined,
    tierliste: Tierliste | null | undefined,
): string[] {
    if (detailLines.length > 0 || !tierliste) {
        return detailLines;
    }
    const kid = Number(kundeId);
    const tierKid = Number(tierliste.kunde_id_zielbetrieb) || Number(tierliste.kunde_id_einstallen);
    if (kid > 0 && kid === tierKid) {
        return buildTierlisteEmpfaengerDetailLines(tierliste);
    }
    return detailLines;
}

/** Name + VVVO/address for Empfänger card (details dialog + stepper). */
export function getExternEmpfaengerDisplayEntries(
    tierliste: Tierliste | null | undefined,
    kontakte: Betriebskontakt[],
    schlachthof: Client | null,
    herkunftFallback?: string | null,
): ExternEmpfaengerDisplayEntry[] {
    if (!hasExternEmpfaenger(tierliste, kontakte, schlachthof)) {
        return [];
    }
    const entries: ExternEmpfaengerDisplayEntry[] = [];
    const seenKontaktIds = new Set<number>();
    const pushKontaktEntry = (k: Betriebskontakt) => {
        const kontaktId = Number(k.kontakt_id);
        if (kontaktId > 0) {
            if (seenKontaktIds.has(kontaktId)) {
                return;
            }
            seenKontaktIds.add(kontaktId);
        }
        const name = formatBetriebskontaktDisplayName(k);
        if (!name) {
            return;
        }
        const detailLines = supplementDetailLinesFromTierliste(
            buildEmpfaengerDetailLines(k),
            k.kunde_id,
            tierliste,
        );
        entries.push({ name, detailLines });
    };

    for (const k of kontakte || []) {
        pushKontaktEntry(k);
    }
    if (schlachthof) {
        const detailLines = supplementDetailLinesFromTierliste(
            buildEmpfaengerDetailLines(schlachthof),
            schlachthof.kunde_id,
            tierliste,
        );
        const name = formatBetriebskontaktDisplayName(schlachthof);
        if (name) {
            entries.push({ name, detailLines });
        }
    }
    if (entries.length === 0) {
        const herkunftEntries = parseEmpfaengerDisplayEntriesFromHerkunft(herkunftFallback);
        const zielName = getTierlisteZielbetriebAnzeigeName(tierliste);
        const tierlisteLabels =
            zielName && zielName !== '—'
                ? zielName.includes(', ')
                    ? zielName.split(', ').map(s => s.trim()).filter(Boolean)
                    : [zielName]
                : [];

        if (herkunftEntries.length > tierlisteLabels.length) {
            return herkunftEntries;
        }

        if (tierlisteLabels.length > 0) {
            const tierDetailLines = buildTierlisteEmpfaengerDetailLines(tierliste);
            for (const label of tierlisteLabels) {
                entries.push({
                    name: label,
                    detailLines: tierlisteLabels.length === 1 ? tierDetailLines : [],
                });
            }
        }
    }
    if (entries.length === 0) {
        return parseEmpfaengerDisplayEntriesFromHerkunft(herkunftFallback);
    }
    return entries;
}

/** Display lines for Empfänger card (details dialog + stepper). */
export function getExternEmpfaengerDisplayLines(
    tierliste: Tierliste | null | undefined,
    kontakte: Betriebskontakt[],
    schlachthof: Client | null,
    herkunftFallback?: string | null,
): string[] {
    return getExternEmpfaengerDisplayEntries(tierliste, kontakte, schlachthof, herkunftFallback).map(
        entry => entry.name,
    );
}
